import type { HelpGuide } from '../types';

export const settingsAccountMappingGuide: HelpGuide = {
  key: 'settings-account-mapping',
  title: 'ตั้งค่าบัญชีอ้างอิง (Account Mapping)',
  subtitle: 'เมนู: ระบบ ▸ ตั้งค่าบัญชีอ้างอิง',
  updated: 'ปรับปรุง 21 มิ.ย. 2569',
  docxUrl: '/help/settings-account-mapping/คู่มือการใช้งาน_ตั้งค่าบัญชีอ้างอิง_DCC-ERP.docx',
  sections: [
    {
      id: 'overview',
      heading: '1. หน้านี้คืออะไร และใช้ทำอะไร',
      blocks: [
        {
          kind: 'para',
          text: 'หน้า “ตั้งค่าบัญชีอ้างอิง (Account Mapping)” ใช้กำหนดว่า “บัญชีอ้างอิง” ของระบบแต่ละตัว (เช่น เงินฝากธนาคารหลัก, ลูกหนี้การค้า, เจ้าหนี้การค้า, ภาษีขาย, รายได้ขายในประเทศ) ผูกกับ รหัสบัญชีจริงในผังบัญชี (COA) 8 หลัก ตัวไหน — ระบบจะใช้ค่าเหล่านี้ตอนสร้างใบสำคัญลงบัญชี (JV) อัตโนมัติในทุกโมดูล',
        },
        {
          kind: 'callout',
          text: 'ทำไมสำคัญ: ทุกครั้งที่ระบบลงบัญชีอัตโนมัติ (ออกใบแจ้งหนี้ลูกหนี้, จ่ายเจ้าหนี้, จ่ายภาษีที่ดิน, ตัดจ่ายล่วงหน้า ฯลฯ) ระบบจะแปลง “บัญชีอ้างอิง” → รหัสบัญชีจริงตามที่ตั้งไว้ในหน้านี้ ถ้าตั้งผิดหรือยังไม่ตั้ง การลงบัญชีของโมดูลนั้นจะผิดหรือทำไม่ได้ จึงเป็นหน้าสำหรับผู้ดูแลระบบเท่านั้น',
          variant: 'warn',
        },
        { kind: 'para', text: 'สิ่งที่ทำได้ในหน้านี้:' },
        {
          kind: 'bullets',
          items: [
            {
              lead: 'ดูบัญชีอ้างอิงทั้งหมด — ',
              text: 'รายการบัญชีอ้างอิงของบริษัทที่เลือก จัดกลุ่มตามหมวด (เงินสด/ธนาคาร, ลูกหนี้, เจ้าหนี้, ภาษี, รายได้, ฯลฯ)',
            },
            {
              lead: 'แก้รหัสบัญชีที่ผูกไว้ — ',
              text: 'เลือกบัญชีจริงจากผังบัญชีให้แต่ละบัญชีอ้างอิง แก้แล้วมีผลทันที',
            },
            {
              lead: 'ตรวจสถานะ — ',
              text: 'ดูว่าบัญชีอ้างอิงตัวไหนตั้งค่าครบ ตัวไหนยังไม่ตั้ง และตัวไหนผูกกับรหัสที่ไม่มีจริงในผังบัญชี',
            },
            {
              lead: 'ค้นหา — ',
              text: 'พิมพ์ชื่อบัญชีอ้างอิง รหัส key หรือรหัสบัญชี เพื่อกรองรายการ',
            },
          ],
        },
        {
          kind: 'image',
          src: '/help/settings-account-mapping/01-overview.png',
          caption: 'ภาพรวมหน้าตั้งค่าบัญชีอ้างอิง — การ์ดสรุป + คำอธิบาย + ตารางจัดกลุ่มตามหมวด',
        },
      ],
    },
    {
      id: 'kpi',
      heading: '2. การ์ดสรุปด้านบน',
      blocks: [
        { kind: 'para', text: 'การ์ด 4 ใบด้านบนสรุปสถานะการตั้งค่าทั้งหมดของบริษัทที่เลือก:' },
        {
          kind: 'bullets',
          items: [
            {
              lead: 'บัญชีอ้างอิงทั้งหมด — ',
              text: 'จำนวนบัญชีอ้างอิงทั้งหมดที่ระบบใช้ลงบัญชีอัตโนมัติ',
            },
            { lead: 'ตั้งค่าแล้ว — ', text: 'จำนวนที่มีบัญชีจริงผูกไว้แล้ว (สีเขียว)' },
            {
              lead: 'ยังไม่ได้ตั้งค่า — ',
              text: 'จำนวนที่ยังไม่มีบัญชีผูก — โมดูลที่ใช้บัญชีอ้างอิงเหล่านี้จะลงบัญชีไม่ได้จนกว่าจะตั้ง (สีส้ม)',
            },
            {
              lead: 'รหัสไม่ถูกต้อง — ',
              text: 'จำนวนที่ผูกกับรหัสที่ไม่มี/ปิดใช้งานในผังบัญชีของบริษัทนี้ ต้องแก้ (สีแดง)',
            },
          ],
        },
        {
          kind: 'image',
          src: '/help/settings-account-mapping/02-kpi.png',
          caption: 'การ์ดสรุป: ทั้งหมด / ตั้งค่าแล้ว / ยังไม่ได้ตั้งค่า / รหัสไม่ถูกต้อง',
        },
      ],
    },
    {
      id: 'table',
      heading: '3. การอ่านตาราง',
      blocks: [
        {
          kind: 'para',
          text: 'รายการถูกจัดกลุ่มตามหมวด — เงินสด/ธนาคาร · ลูกหนี้ · เจ้าหนี้ · ภาษี (VAT/หัก ณ ที่จ่าย/เงินได้) · รายได้/ต้นทุนขาย · ค่าใช้จ่าย · สินทรัพย์อื่น · ส่วนของผู้ถือหุ้น · ผลประโยชน์พนักงาน · ภาษีที่ดิน&ป้าย · งานระหว่างก่อสร้าง · สินทรัพย์ถาวร · สัญญาเช่า TFRS 16',
        },
        { kind: 'para', text: 'แต่ละแถวมี 3 คอลัมน์:' },
        {
          kind: 'bullets',
          items: [
            {
              lead: 'บัญชีอ้างอิง — ',
              text: 'ชื่อภาษาไทยของบัญชีอ้างอิง พร้อมรหัส key ของระบบด้านล่าง (เช่น BANK_PRIMARY, AR_TRADE)',
            },
            {
              lead: 'รหัสบัญชีในผังบัญชี (COA) — ',
              text: 'ช่องเลือกบัญชีจริง 8 หลักที่ผูกอยู่ (แก้ได้)',
            },
            { lead: 'สถานะ — ', text: 'ป้ายบอกสถานะ: ใช้งานได้ / ยังไม่ตั้งค่า / รหัสไม่ถูกต้อง' },
          ],
        },
        {
          kind: 'image',
          src: '/help/settings-account-mapping/03-table.png',
          caption: 'ตารางหมวดเงินสด/ธนาคาร — แต่ละแถว = บัญชีอ้างอิง + รหัส COA ที่ผูก + สถานะ',
        },
      ],
    },
    {
      id: 'edit',
      heading: '4. วิธีแก้ไขบัญชีอ้างอิง',
      blocks: [
        {
          kind: 'steps',
          items: [
            {
              title: 'หาแถวที่ต้องการแก้',
              text: 'เลื่อนหาในหมวดที่เกี่ยวข้อง หรือพิมพ์ค้นหาที่มุมขวาบน (เช่น พิมพ์ “ธนาคาร” หรือ “BANK”)',
            },
            {
              title: 'กดที่ช่องรหัสบัญชี',
              text: 'ในคอลัมน์ “รหัสบัญชีในผังบัญชี (COA)” กดที่ช่องเลือก จะมีรายการบัญชีจริงทั้งหมดของบริษัทให้ค้นหา — พิมพ์รหัสหรือชื่อบัญชีเพื่อกรอง',
            },
            {
              title: 'เลือกบัญชีจริงที่ถูกต้อง',
              text: 'เลือกบัญชี 8 หลักที่ต้องการ ระบบบันทึกทันทีและขึ้นป้าย “บันทึกแล้ว” สีเขียว',
            },
            {
              title: 'มีผลทันที',
              text: 'การลงบัญชี (JV) ครั้งถัดไปของทุกโมดูลจะใช้รหัสใหม่นี้ทันที (ระบบล้างแคชให้อัตโนมัติ) ไม่ต้องรีสตาร์ท',
            },
          ],
        },
        {
          kind: 'callout',
          text: 'ระบบยอมรับเฉพาะบัญชีที่มีอยู่จริงและเปิดใช้งานในผังบัญชีของบริษัทนี้เท่านั้น ถ้าเลือกบัญชีที่ปิดใช้งานหรือไม่มีจริง ระบบจะแจ้งเตือนและไม่บันทึก',
          variant: 'info',
        },
        {
          kind: 'image',
          src: '/help/settings-account-mapping/04-edit.png',
          caption: 'กดช่องรหัสบัญชี → ค้นหาและเลือกบัญชีจริงจากผังบัญชี',
        },
      ],
    },
    {
      id: 'status',
      heading: '5. สถานะ และการแก้ปัญหา',
      blocks: [
        {
          kind: 'bullets',
          items: [
            {
              lead: 'ใช้งานได้ (เขียว) — ',
              text: 'ผูกกับบัญชีที่มีจริงและเปิดใช้งาน ใช้ลงบัญชีได้ปกติ',
            },
            {
              lead: 'ยังไม่ตั้งค่า (ส้ม) — ',
              text: 'ยังไม่มีบัญชีผูก โมดูลที่ใช้บัญชีอ้างอิงตัวนี้จะลงบัญชีไม่ได้ ให้เลือกบัญชีให้ครบ',
            },
            {
              lead: 'รหัสไม่ถูกต้อง (แดง) — ',
              text: 'ผูกกับรหัสที่ไม่มีหรือถูกปิดใช้งานในผังบัญชี — มักเกิดจากการย้ายข้อมูลข้ามบริษัท ให้เลือกบัญชีใหม่ที่ถูกต้อง',
            },
          ],
        },
        {
          kind: 'callout',
          text: 'ตัวอย่างบัญชีอ้างอิงสำคัญที่ควรตั้งให้ครบ: BANK_PRIMARY (บัญชีธนาคารหลัก — ใช้เป็นบัญชีจ่าย/รับเงินเริ่มต้นของหลายโมดูล) และ SALES_DOMESTIC (รายได้ขายในประเทศ — ใช้เป็นบัญชีรายได้เริ่มต้นของใบแจ้งหนี้ลูกหนี้)',
          variant: 'warn',
        },
        {
          kind: 'image',
          src: '/help/settings-account-mapping/05-status.png',
          caption: 'ป้ายสถานะแต่ละแบบ และตัวอย่างบัญชีอ้างอิงที่ยังไม่ได้ตั้งค่า',
        },
      ],
    },
    {
      id: 'faq',
      heading: '6. คำถามที่พบบ่อย',
      blocks: [
        {
          kind: 'bullets',
          items: [
            {
              lead: 'หน้านี้ต่างจาก “ตั้งค่าผังบัญชี” อย่างไร? — ',
              text: 'หน้า “ตั้งค่าผังบัญชี” ใช้เปิด/ปิดการใช้งานบัญชีในผังบัญชี ส่วนหน้านี้ใช้ “ผูก” บัญชีอ้างอิงของระบบเข้ากับรหัสบัญชีจริงที่ใช้ลงบัญชีอัตโนมัติ',
            },
            {
              lead: 'แก้แล้วต้องทำอะไรเพิ่มไหม? — ',
              text: 'ไม่ต้อง บันทึกแล้วมีผลกับการลงบัญชีครั้งถัดไปทันที (เอกสารที่ลงบัญชีไปแล้วไม่เปลี่ยนย้อนหลัง)',
            },
            {
              lead: 'ตั้งค่าแยกแต่ละบริษัทได้ไหม? — ',
              text: 'ได้ ค่าทั้งหมดเป็นรายบริษัท สลับบริษัทที่มุมบนซ้ายแล้วตั้งค่าของแต่ละบริษัทแยกกัน',
            },
            {
              lead: 'ใครแก้ได้? — ',
              text: 'เฉพาะผู้ดูแลระบบ (สิทธิ์ account-mapping.manage) ผู้ใช้ทั่วไปจะเห็นค่าปัจจุบันแบบอ่านอย่างเดียว',
            },
          ],
        },
      ],
    },
  ],
};
